<template>
  <modal
    :show="isVisible"
    :title="t('Recusar Solicitação de Reembolso')"
    :close-label="t('Cancelar')"
    :save-label="t('Recusar')"
    :save-fn="handleDecline"
    :save-disabled-fn="() => !reason.trim() || isProcessing"
    size="small"
    elem-id="modal_payroll-refunds-decline"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <!-- Content only renders when modal is visible (prevents API calls on mount) -->
    <template v-if="isVisible">
      <box type="warning" :title="t('Motivo obrigatório')">
        <p>{{ t('Você deve fornecer um motivo para recusar esta solicitação de reembolso.') }}</p>

        <div class="refund-info" v-if="refundData">
          <div class="info-row">
            <span class="info-label">{{ t('Título') }}:</span>
            <span class="info-value">{{ refundData.title }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ t('Valor') }}:</span>
            <span class="info-value">{{ n(refundData.amount_display ?? refundData.amount ?? 0, 'currency') }}</span>
          </div>
        </div>

        <input-text
          v-model="reason"
          elem-id="refund-decline-reason"
          :label="''"
          :placeholder="t('Informe o motivo da recusa...')"
          :multiline="true"
          :rows="4"
          class="reason-input"
          data-testid="payroll-refunds-decline-reason-input"
        />

        <small v-if="showError && !reason.trim()" class="p-error">
          {{ t('O motivo é obrigatório') }}
        </small>
      </box>

      <div v-if="isProcessing" class="text-center" style="margin-top: 15px;">
        <spinner style="width: 1rem; height: 1rem" /> {{ t('Recusando solicitação...') }}
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../../utils/DebugLogger';
import { eventBus } from '../../../eventBus';
import { usePayrollRefunds } from '../../../composables/usePayrollRefunds';

type Id = number | string;

interface RefundData {
  id: Id;
  title?: string;
  amount?: number;
  amount_display?: number;
  status?: number;
  [key: string]: unknown;
}

interface DeclineSuccessPayload {
  refundId: Id;
  reason: string;
}

const emit = defineEmits<{
  'close': [];
  'decline-success': [payload: DeclineSuccessPayload];
}>();

const { t, n } = useI18n();
const { declineRefund } = usePayrollRefunds();

const isVisible = ref<boolean>(false);
const refundData = ref<RefundData | null>(null);
const reason = ref<string>('');
const isProcessing = ref<boolean>(false);
const showError = ref<boolean>(false);

const open = (refund: RefundData): void => {
  log('PayrollRefundsDeclineModal', 'open', 'Opening decline modal for refund', refund?.id);
  refundData.value = refund;
  reason.value = '';
  showError.value = false;
  isVisible.value = true;
};

const close = (): void => {
  log('PayrollRefundsDeclineModal', 'close', 'Closing decline modal');
  isVisible.value = false;
  refundData.value = null;
  reason.value = '';
  showError.value = false;
  emit('close');
};

const handleClose = (): void => {
  close();
};

const handleModalOpen = (data?: unknown): void => {
  log('PayrollRefundsDeclineModal', 'handleModalOpen', 'Standard modal open event received', data);
  if (data) {
    open(data as RefundData);
  }
};

const handleDecline = async (): Promise<void> => {
  log('PayrollRefundsDeclineModal', 'handleDecline', 'Declining refund', refundData.value?.id);

  if (!reason.value.trim()) {
    showError.value = true;
    eventBus.emit('globalMessage.error', t('Você deve fornecer um motivo para recusar a solicitação'));
    return;
  }

  isProcessing.value = true;

  try {
    const result = await (declineRefund as unknown as (id: Id, reason: string) => Promise<unknown>)(refundData.value!.id, reason.value.trim());

    if (result) {
      eventBus.emit('globalMessage.success', {
        message: t('Solicitação recusada com sucesso'),
        duration: 3000
      });
      emit('decline-success', { refundId: refundData.value!.id, reason: reason.value.trim() });
      eventBus.emit('payroll-refunds.refresh');
      close();
    } else {
      eventBus.emit('globalMessage.error', {
        message: t('Erro ao recusar solicitação'),
        duration: 5000
      });
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    log('PayrollRefundsDeclineModal', 'handleDecline', 'Error declining refund', error);
    eventBus.emit('globalMessage.error', t('Erro ao recusar solicitação: ') + message);
  } finally {
    isProcessing.value = false;
  }
};

onMounted(() => {
  log('PayrollRefundsDeclineModal', 'onMounted', 'Component mounted, setting up event listeners');
  eventBus.on('modal.payroll-refunds-decline.open', handleModalOpen);
  eventBus.on('modal.payroll-refunds-decline.close', close);
});

onBeforeUnmount(() => {
  log('PayrollRefundsDeclineModal', 'onBeforeUnmount', 'Component unmounting, cleaning up event listeners');
  eventBus.off('modal.payroll-refunds-decline.open', handleModalOpen);
  eventBus.off('modal.payroll-refunds-decline.close', close);
});

defineExpose({ open, close });
</script>

<style scoped>
.refund-info {
  background-color: var(--surface-50);
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.info-row {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.info-row:last-child {
  margin-bottom: 0;
}

.info-label {
  font-weight: 600;
  color: var(--text-color-secondary);
}

.info-value {
  color: var(--text-color);
}

.reason-input {
  width: 100%;
  margin-top: 1rem;
}

.text-center {
  text-align: center;
}
</style>
